import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteCQA,
  fetchCQADesData,
  updateCQAData,
} from "../../redux/actions/CQAAction";
import { MdOutlineDeleteForever } from "react-icons/md";
import { FaRegEdit } from "react-icons/fa";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import PageComponent from "../../Components/PageComponent";
import Page from "../../Components/Page";

const CQAList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Fetching state from Redux
  const { data: cqaList = [], loading, error } = useSelector(
    (state) => state.CQA.CQA || {}
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedCQA, setSelectedCQA] = useState(null);
  const [newFile, setNewFile] = useState(null); // Handle new file uploads
  const [isUpdating, setIsUpdating] = useState(false); // Update feedback
  const [isDeleting, setIsDeleting] = useState(false); // Delete feedback

  // Fetch CQA data on component mount
  useEffect(() => {
    dispatch(fetchCQADesData());
  }, [dispatch]);

  const handleEdit = (cqa) => {
    setSelectedCQA({ ...cqa }); // Clone object to avoid mutating original state
    setIsModalOpen(true);
  };

  const handleDelete = (cqa) => {
    setSelectedCQA(cqa);
    setIsDeleteModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCQA(null);
    setNewFile(null);
  };

  const handleDeleteCloseModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedCQA(null);
  };

  const handleFileRemove = () => {
    setSelectedCQA({ ...selectedCQA, files: null });
    setNewFile(null);
  };

  const handleSaveChanges = async (e) => {
    e.preventDefault();
    setIsUpdating(true);

    if (!selectedCQA?.id || !selectedCQA?.title || !selectedCQA?.description) {
      alert("All fields are required.");
      setIsUpdating(false);
      return;
    }

    const formData = new FormData();
    formData.append("title", selectedCQA.title);
    formData.append("description", selectedCQA.description);
    if (newFile) formData.append("file", newFile);

    await dispatch(updateCQAData(selectedCQA.id, formData));
    setIsUpdating(false);
    dispatch(fetchCQADesData());
    handleCloseModal();
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    if (selectedCQA) {
      await dispatch(deleteCQA(selectedCQA.id));
      setIsDeleting(false);
      dispatch(fetchCQADesData());
      handleDeleteCloseModal();
    }
  };

  if (loading) {
    return <div className="text-center text-gray-700">Loading...</div>;
  }

  if (error) {
    return (
      <div className="text-center text-red-600">
        {error.message || "An unexpected error occurred."}
      </div>
    );
  }

  return (
    <>
            <Page>
    <div className="flex flex-col p-6 bg-[#F8F7FA] h-screen">
      {/* Header Section */}
      <div className="mb-6">
      <div className="flex items-center space-x-2 text-3xl text-gray-500">
          <span>Gallery & News</span>
          <span>/</span>
          <span className="text-black">Center for Quality Assurance (CQA)</span>
        </div>
        <div className="flex items-center mt-4 space-x-4">
          <button
            className="px-4 py-2 text-[#92288C] border border-[#92288C] rounded-md shadow hover:bg-gray-100"
            onClick={() => navigate("/gallery&News/cqa-description")}
          >
            Add Description
          </button>
          <button
            className="px-4 py-2 text-[#92288C] border border-[#92288C] rounded-md shadow hover:bg-gray-100"
            onClick={() => navigate("/gallery&News/add-cqa")}
          >
            Add New Resource
          </button>
          <button className="px-4 py-2 text-white bg-[#92288C] rounded-md shadow hover:bg-[#6C0B67]">
            Resource List
          </button>
          <button
            className="px-4 py-2 text-[#92288C] border border-[#92288C] rounded-md shadow hover:bg-gray-100"
            onClick={() => navigate("/gallery&News/cqa-staff")}
          >
            CQA Staff
          </button>
        </div>
      </div>

      {/* CQA Table Section */}
      <div className="overflow-hidden bg-white rounded-lg shadow-md">
        <table className="w-full border-collapse table-auto">
          <thead className="bg-gray-100 border-b">
            <tr>
              <th className="px-6 py-4 font-medium text-left text-gray-700">
                Title
              </th>
              <th className="px-6 py-4 font-medium text-left text-gray-700">
                Files
              </th>
              <th className="px-6 py-4 font-medium text-left text-gray-700">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {cqaList.length > 0 ? (
              cqaList.map((cqa, index) => (
                <tr
                  key={cqa.id}
                  className={`border-b ${
                    index % 2 === 0 ? "bg-gray-50" : "bg-white"
                  }`}
                >
                  <td className="px-6 py-4 text-gray-800">{cqa.title}</td>
                  <td className="px-6 py-4 text-gray-600">
                    {cqa.files ? (
                      <a
                        href={cqa.files}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 underline"
                      >
                        file
                      </a>
                    ) : (
                      "No file available"
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-4">
                      <button
                        type="button"
                        className="flex items-center px-5 py-1 text-gray-400 bg-gray-100 border border-gray-400 rounded"
                        onClick={() => handleDelete(cqa)}
                      >
                        Delete
                        <MdOutlineDeleteForever className="w-5 h-5 ml-2" />
                      </button>
                      <button
                        type="button"
                        className="flex items-center px-5 py-1 border rounded text-primary-color border-primary-color bg-primary-color bg-opacity-10"
                        onClick={() => handleEdit(cqa)}
                      >
                        Edit
                        <FaRegEdit className="w-4 h-4 ml-2" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="3"
                  className="px-6 py-4 font-medium text-center text-gray-600"
                >
                  No CQA data available.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Edit Modal */}
      {isModalOpen && selectedCQA && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-1/2 p-6 bg-white rounded-lg shadow-lg">
            <h2 className="mb-4 text-xl font-bold">Edit</h2>
            <form onSubmit={handleSaveChanges}>
              <div className="space-y-4">
                <div>
                  <label className="block mb-2 font-medium text-gray-700">
                    Title
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
                    value={selectedCQA.title}
                    onChange={(e) =>
                      setSelectedCQA({ ...selectedCQA, title: e.target.value })
                    }
                    placeholder="Resource Name"
                  />
                </div>
                <div>
                  <label className="block mb-2 font-medium text-gray-700">
                    Description
                  </label>
                  <ReactQuill
                    value={selectedCQA.description || ""}
                    onChange={(value) =>
                      setSelectedCQA({ ...selectedCQA, description: value })
                    }
                    className="bg-white border border-gray-300 rounded-md"
                    theme="snow"
                  />
                </div>
                <div>
                  <label className="block mb-2 font-medium text-gray-700">
                    Upload Document
                  </label>
                  {selectedCQA.files && selectedCQA.files.length > 0 ? (
                    <>
                      <a
                        href={selectedCQA.files}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 underline"
                      >
                        View File
                      </a>
                      <button
                        type="button"
                        className="px-4 py-2 text-red-500 bg-red-100 rounded-md hover:bg-red-200"
                        onClick={handleFileRemove}
                      >
                        Remove
                      </button>
                    </>
                  ) : (
                    <input
                      type="file"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
                      // accept=".pdf,.docx"
                      onChange={(e) => setNewFile(e.target.files[0])}
                    />
                  )}
                </div>
              </div>
              <div className="flex justify-end mt-6 space-x-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`px-4 py-2 text-white rounded-md ${
                    isUpdating
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-[#92288C] hover:bg-[#6C0B67]"
                  }`}
                  disabled={isUpdating}
                >
                  {isUpdating ? "Updating..." : "Update"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {isDeleteModalOpen && selectedCQA && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-[400px] text-center">
            <h3 className="mb-4 text-xl font-semibold text-gray-700">
              Delete?
            </h3>
            <p className="mb-6 text-gray-600">
              Are you sure you want to delete this CQA resource?
              <br />
              <strong>{selectedCQA.title}</strong>
            </p>
            <div className="flex justify-center gap-4">
              <button
                className="px-6 py-2 text-gray-500 bg-gray-200 rounded-md hover:bg-gray-300"
                onClick={handleDeleteCloseModal}
              >
                No
              </button>
              <button
                className={`px-6 py-2 text-white rounded-md ${
                  isDeleting ? "bg-gray-400 cursor-not-allowed" : "bg-[#92288C]"
                }`}
                onClick={confirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Yes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </Page>
    </>
  );
};

export default CQAList;

